import { Box, Collapse, Flex } from '@mantine/core';
import { useClickOutside, useDisclosure } from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import { SiestaAction, SiestaActionType } from '../types';
import { AIActionCard } from './AIActionCard';
import { AISetTitle } from './AISetTitle';
import { StackedCards } from './StackedCards';

type Props = {
  actions: SiestaAction[];
  onActionClick?: (action: SiestaAction) => void;
  collapsible?: boolean;
  title?: string;
};

export function AIActionSet({ actions, onActionClick, collapsible, title }: Props) {
  const [opened, { toggle, close }] = useDisclosure(false);
  const clickOutsideRef = useClickOutside(close);
  const responsiveWidth = 'min(374px, calc(100% - 40px))';

  const renderCard = (action: SiestaAction) => (
    <AIActionCard
      key={action.id}
      label={action.label}
      description={action.description}
      descriptionMode={action.descriptionMode}
      href={action.type === SiestaActionType.LINK ? action.href : undefined}
      onClick={action.type === SiestaActionType.CHAT ? () => onActionClick?.(action) : undefined}
    />
  );

  if (collapsible) {
    const [firstAction, ...restActions] = actions;
    const behindCount = Math.min(2, restActions.length);

    return (
      <Flex ref={clickOutsideRef} direction="column" align="center" w={responsiveWidth} maw={374}>
        <AISetTitle
          label={title}
          onClick={toggle}
          rightSection={
            <IconChevronDown
              size={12}
              color="white"
              style={{
                transform: opened ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 200ms ease',
              }}
            />
          }
        />
        <Box w="100%" mt="xxs">
          <Collapse in={opened}>
            <Flex direction="column" gap="xxxs">
              {actions.map(renderCard)}
            </Flex>
          </Collapse>
          {!opened && firstAction && (
            <StackedCards action={firstAction} behindCount={behindCount} onExpand={toggle} />
          )}
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="column" align="center" w={responsiveWidth} maw={374}>
      <AISetTitle label={title} />
      <Flex direction="column" gap="xxxs" mt="xxs" style={{ width: '100%' }}>
        {actions.map(renderCard)}
      </Flex>
    </Flex>
  );
}
